/**
 * Case study content, shared by the Home teaser and the Work page so the two
 * can never drift apart. Every claim, metric and module name here is taken
 * from the capabilities deck or from the reporting screenshot the page itself
 * displays. Do not add numbers that aren't sourced from a real program, and
 * keep them consistent with whatever chart is rendered alongside them.
 *
 * `url`/`href` are optional: omit both when a program's site is no longer
 * live, and the header simply drops that line.
 */

import towingHero from '../images/work-towing-bootcamp.jpg';
import towingCalendar from '../images/towing-booking-calendar.jpg';
import towingEmail from '../images/towing-confirmation-email.jpg';
import motorradHero from '../images/work-motorrad-days.jpg';
import motorradCheckin from '../images/motorrad-checkin-mobile.jpg';
import motorradDashboard from '../images/motorrad-dashboard.jpg';
import autoShowBooth from '../images/event-activation-booth.jpg';
import autoShowKiosk from '../images/event-qr-kiosk.jpg';
import lookupPrefill from '../images/badge-scan-flow.png';

const caseStudies = [
  {
    id: 'towing-bootcamp',
    client: 'Ford',
    title: 'Ford Towing Bootcamp',
    tagline: 'Paid, bookable training experiences sold pre-event and on-site.',
    summary:
      'A hands-on towing course that attendees book, pay for and attend across multiple RV and auto shows, with scheduling, guest management and payment handled end to end.',
    modules: ['Reserve', 'Relay', 'Retail'],
    points: [
      'Flexible booking with multi-module and guest options',
      'Multi-stage customer outreach and data collection flows',
      'Multi-channel purchase flows, pre-event and on-site',
    ],
    metrics: [],
    hero: {
      src: towingHero,
      width: 1300,
      height: 1085,
      alt: 'Ford Towing Bootcamp registration microsite showing a truck towing a trailer',
    },
    media: [
      {
        src: towingCalendar,
        width: 1171,
        height: 721,
        alt: 'Booking step with a February calendar and 9:00am / 11:00am time slots',
        caption: 'Date and time selection flows straight into payment.',
      },
      {
        src: towingEmail,
        width: 1094,
        height: 1270,
        alt: 'Ford-branded booking confirmation email with session details',
        caption: 'Confirmation built from the attendee’s own booking details.',
      },
    ],
  },
  {
    id: 'motorrad-days',
    client: 'BMW Motorrad',
    title: 'BMW Motorrad Days Americas',
    tagline:
      'One registration spanning four activations at a three-day festival.',
    summary:
      'Demo rides, a parade lap, a VIP package and a museum night. Each has its own capacity, its own check-in and its own question set, all behind a single attendee profile.',
    modules: ['Reserve', 'Relay', 'Report'],
    points: [
      'Pre-event and on-site registration for multiple activations',
      'Easy on-site check-in to track guest journey at the event',
      'Custom question sets and communications based on interest and attendance',
    ],
    metrics: [],
    hero: {
      src: motorradHero,
      width: 1300,
      height: 1273,
      alt: 'BMW Motorrad Days Americas 2025 event microsite',
    },
    media: [
      {
        src: motorradCheckin,
        width: 608,
        height: 1060,
        alt: 'Mobile check-in page showing a QR code and demo ride requirements',
        caption: 'Attendees pull up a QR code for quick on-site check-in.',
      },
      {
        src: motorradDashboard,
        width: 1400,
        height: 877,
        alt: 'Live dashboard with registration counts and a per-attendee activation table',
        caption: 'Live counts per activation, down to the individual attendee.',
      },
    ],
  },
  {
    id: 'auto-show-circuit',
    client: 'Ford',
    title: 'Auto Show Activation Circuit',
    url: 'Multi-activation program',
    href: null,
    tagline: 'Register once at any activation, recognized at all of them.',
    summary:
      'A show floor running several activations at the same time, each with its own staff, its own questions and its own queue. Attendees sign up at whichever one they reach first, by QR code, self-service kiosk or brand ambassador. At every activation after that they are looked up in seconds instead of filling in the same form again.',
    modules: ['Reserve', 'Relay', 'Report'],
    points: [
      'QR codes, kiosks and roaming brand ambassadors all feed one shared profile',
      'Instant lookup at any activation, so returning attendees never re-enter their details',
      'Progressive profiling adds to what you already know instead of asking twice',
    ],
    metrics: [],
    hero: {
      src: autoShowBooth,
      width: 974,
      height: 654,
      alt: 'Ford-branded activation booth on an auto show floor',
    },
    media: [
      {
        src: autoShowKiosk,
        width: 545,
        height: 823,
        alt: 'Attendee scanning a QR code at a self-service kiosk on the show floor',
        caption: 'Any touchpoint can start a registration, staffed or not.',
      },
      {
        src: lookupPrefill,
        width: 1400,
        height: 576,
        alt: 'An empty capture form, a QR scan, then the same form already filled in',
        caption: 'One scan pulls an existing profile back, already filled in.',
      },
    ],
  },
];

export default caseStudies;
